import React, { useState } from 'react';
import { useMonthlyRotaForm } from '../context/MonthlyRotaContext';
import { FaSearch, FaUserPlus } from 'react-icons/fa';

const Step2StaffSelection = () => {
  const { formData, updateField } = useMonthlyRotaForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [deptFilter, setDeptFilter] = useState('All Departments');

  // Exhaustive list mirroring the blueprint matrix data layout
  const staffMasterList = [
    { id: '1', name: 'Ahmed Raza', role: 'Head Chef', department: 'Kitchen', type: 'Full Time', status: 'Active', initials: 'AR' },
    { id: '2', name: 'Sara Khan', role: 'Server', department: 'Front of House', type: 'Full Time', status: 'Active', initials: 'SK' },
    { id: '3', name: 'John Smith', role: 'Kitchen Assistant', department: 'Kitchen', type: 'Part Time', status: 'Active', initials: 'JS' },
    { id: '4', name: 'Emily White', role: 'Server', department: 'Front of House', type: 'Part Time', status: 'Active', initials: 'EW' },
    { id: '5', name: 'Usman Ali', role: 'Manager', department: 'Management', type: 'Full Time', status: 'Active', initials: 'UA' },
    { id: '6', name: 'Zainab Butt', role: 'Cleaner', department: 'Cleaning', type: 'Part Time', status: 'Active', initials: 'ZB' }
  ];

  const handleSelectStaff = (staffId) => {
    const current = [...formData.selectedStaffIds];
    if (current.includes(staffId)) {
      updateField('selectedStaffIds', current.filter(id => id !== staffId));
    } else {
      updateField('selectedStaffIds', [...current, staffId]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      updateField('selectedStaffIds', filteredStaff.map(s => s.id));
    } else {
      updateField('selectedStaffIds', []);
    }
  };

  const filteredStaff = staffMasterList.filter(staff => {
    const matchesSearch = staff.name.toLowerCase().includes(searchTerm.toLowerCase()) || staff.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'All Roles' || staff.role === roleFilter;
    const matchesStatus = statusFilter === 'All Statuses' || staff.status === statusFilter;
    const matchesDept = deptFilter === 'All Departments' || staff.department === deptFilter;
    return matchesSearch && matchesRole && matchesStatus && matchesDept;
  });

  return (
    <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center text-white font-bold text-sm">2</div>
          <div>
            <h2 className="text-md font-bold text-gray-900">Staff Selection</h2>
            <p className="text-[11px] text-gray-400">Select which staff members to include in this rota.</p>
          </div>
        </div>
        <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold transition">
          <FaUserPlus size={11} /> Add Staff
        </button>
      </div>

      {/* Query Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative">
          <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
          <input 
            type="text" 
            placeholder="Search staff members..." 
            className="w-full text-xs pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none">
          <option>All Roles</option>
          <option>Head Chef</option>
          <option>Server</option>
          <option>Kitchen Assistant</option>
          <option>Manager</option>
          <option>Cleaner</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none">
          <option>All Statuses</option>
          <option>Active</option>
        </select>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="text-xs p-2 border border-gray-300 rounded-md bg-white focus:outline-none">
          <option>All Departments</option>
          <option>Kitchen</option>
          <option>Front of House</option>
          <option>Management</option>
          <option>Cleaning</option>
        </select>
      </div>

      {/* Selection Matrix Table Layout */}
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-left text-xs font-medium">
          <thead className="bg-slate-50 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="p-3 w-12 text-center">
                <input 
                  type="checkbox" 
                  className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 cursor-pointer"
                  onChange={handleSelectAll}
                  checked={filteredStaff.length > 0 && filteredStaff.every(s => formData.selectedStaffIds.includes(s.id))}
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Role</th>
              <th className="p-3">Department</th>
              <th className="p-3">Employment Type</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-gray-600">
            {filteredStaff.map((staff) => {
              const isChecked = formData.selectedStaffIds.includes(staff.id);
              return (
                <tr key={staff.id} className={`hover:bg-slate-50 transition ${isChecked ? 'bg-blue-50/20' : ''}`}>
                  <td className="p-3 text-center">
                    <input 
                      type="checkbox" 
                      className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 cursor-pointer"
                      checked={isChecked}
                      onChange={() => handleSelectStaff(staff.id)}
                    />
                  </td>
                  <td className="p-3 font-bold text-gray-900">
                    <div className="flex items-center gap-2">
                      <div className="w-5.5 h-5.5 rounded-full bg-slate-100 text-slate-600 font-black flex items-center justify-center text-[9px] border border-gray-200">
                        {staff.initials}
                      </div>
                      <span>{staff.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-gray-500">{staff.role}</td>
                  <td className="p-3 text-gray-500">{staff.department}</td>
                  <td className="p-3 text-gray-500">{staff.type}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" /> {staff.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Step2StaffSelection;
